import { formatPercent, Loan } from '@nemuru-eng/js-api-client';
import { Insurance } from '@nemuru-eng/js-api-client/slim';

import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { LoanConditions } from '@/components/modules/Application/components/PaymentMethod/_shared/types/LoanConditions';
import { AutoLoanConditions } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/types/AutoLoanConditions';
import { LoanConditionsEntity } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/entities/LoanConditionsEntity';
import { SetupFeeEntity } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/entities/SetupFeeEntity';
import { LoanInsuranceMap } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LoanConditions/maps/loanInsurance.map';

type Conditions = ReturnType<typeof getConditionsFromLoan>;

const getConditionsFromLoan = (loan: Loan) => {
  const loanConditions = new LoanConditionsEntity(loan);

  const insurance = LoanInsuranceMap.fromLoan(loan) as Insurance | null;

  return {
    principal: loanConditions.principal,
    term: loanConditions.term,
    instalmentTotalAmount: loanConditions.userDefinedQuota,
    setupFee: {
      type: loanConditions.setupFeeType,
      relative: loanConditions.setupFee.relative.string,
      amount: loanConditions.setupFee.total.string,
    },
    annualInterest: formatPercent(loanConditions.annualInterest ?? 0),
    apr: formatPercent(loanConditions.tae),
    totalAmount: loanConditions.totalAmountPaid,
    ...(insurance && { insurance }),
  };
};

const getConditionsFromPaymentMethodConditions = (conditions: LoanConditions | AutoLoanConditions): Conditions => {
  const setupFee = SetupFeeEntity.fromLoanConditions(conditions);

  const insurance = 'insurance' in conditions ? conditions.insurance : null;

  return {
    principal: conditions.principal.string,
    term: conditions.term ?? null,
    instalmentTotalAmount: conditions.instalmentTotalAmount.string,
    setupFee: {
      type: conditions.setupFeeType ?? null,
      relative: setupFee.relative.string,
      amount: setupFee.total.string,
    },
    annualInterest: formatPercent(conditions.annualInterest?.value ?? 0),
    apr: formatPercent(conditions.apr.value),
    totalAmount: conditions.totalAmount.string,
    ...(insurance && { insurance }),
  };
};

const withLoanInsurance = (conditions: Conditions, loan?: Loan): Conditions => {
  if (conditions.insurance || !loan) {
    return conditions;
  }

  const insurance = LoanInsuranceMap.fromLoan(loan) as Insurance | null;

  if (!insurance) {
    return conditions;
  }

  return { ...conditions, insurance };
};

export const getConditions = (paymentMethod: AbstractLoanPaymentMethod, loan?: Loan) => {
  const term = loan?.loanConditions.loanCalculator.term.period ?? paymentMethod.selectedTerm;
  const lenderName = loan?.loanApplicationSelected?.lenderName;

  if (!term) {
    return null;
  }

  const candidateConditions = paymentMethod.selectedConditionsGroup?.conditions?.filter(
    (condition) => condition.term === term && (!lenderName || condition.providerName === lenderName),
  );

  if (candidateConditions && candidateConditions.length === 1) {
    return withLoanInsurance(getConditionsFromPaymentMethodConditions(candidateConditions[0]), loan);
  }

  if (loan) {
    return getConditionsFromLoan(loan);
  }

  const firstCondition = candidateConditions?.[0];

  if (firstCondition) {
    return withLoanInsurance(getConditionsFromPaymentMethodConditions(firstCondition), loan);
  }

  return null;
};
